import { useCallback, useRef, useState } from "react";

const LIMIT = 100;

/**
 * useState with undo/redo history.
 *   const [value, set, { undo, redo, canUndo, canRedo, reset }] = useUndoableState(init);
 * `set(updater, { coalesceKey })` — consecutive sets with the same coalesceKey
 * (e.g. typing in one field) merge into a single undo step.
 */
export default function useUndoableState(initial) {
  const [state, setState] = useState({ past: [], present: initial, future: [] });
  const lastKey = useRef(null);

  const set = useCallback((updater, opts = {}) => {
    setState((s) => {
      const next = typeof updater === "function" ? updater(s.present) : updater;
      if (next === s.present) return s;
      const coalesce = opts.coalesceKey && opts.coalesceKey === lastKey.current;
      lastKey.current = opts.coalesceKey || null;
      return {
        past: coalesce ? s.past : [...s.past, s.present].slice(-LIMIT),
        present: next,
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    lastKey.current = null;
    setState((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s));
  }, []);

  const redo = useCallback(() => {
    lastKey.current = null;
    setState((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s));
  }, []);

  const reset = useCallback((value) => {
    lastKey.current = null;
    setState({ past: [], present: value, future: [] });
  }, []);

  return [state.present, set, { undo, redo, reset, canUndo: state.past.length > 0, canRedo: state.future.length > 0 }];
}
